/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useEffect, useState } from 'react';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { getAllElements } from 'api/element-api';
import {
  setSelectedElementDetails,
  setReOrderedColumnOptions,
  setReportPreviewPageDisplayOptions,
} from 'store/actions/report-preview-page-actions';
import {
  onToggleSyncScroll,
  resetSyncScrolling,
} from 'store/actions/preview-report-page-actions';
import { clearSelectedElementsContentPanel } from 'store/actions/action-creators';
import {
  disableRefreshIconAction,
  fetchOCRSectionContent,
  fetchSectionContent,
  setSyncScrolling,
} from 'store/actions/preview-report-page-actions';
import { ReactComponent as RefreshIcon } from 'icons/refresh-heading.svg';
import ToolTip from 'components/common/tool-tip-component';
import { scrollElementIntoView } from 'utils/scrolling-utils';
import { isNullOrUndefined } from 'utils/object-utils';
import ReportPreviewPageDisplayOptions from './report-preview-page-display-options';
import { displayOptionsConfig } from 'constants/feature/report-preview-page-contants';
import {
  getElementReoprtColumns,
  getElementListBySections,
} from 'pages/report-preview/report-preview-utils';
import { OPTIONS_CHECKBOX_ARRAY } from 'constants/feature/report-preview-page-contants';
import SlideToggle from 'components/common/slide-toggle-component';
import ReactGrid from './react-window-table';

export const REPORT_PREVIEW_RIGHT_BLOCK = 'report-preview-page-right-panel';
export const REPORT_PREVIEW_RIGHT_BLOCK_ID =
  'report-preview-page-right-panel-id';
const REFRESH_LIST_ICON_SIZE = '25px';

const ReportPreviewRightSideView = ({ toggleViews }) => {
  const dispatch = useDispatch();
  const data = useSelector((state) => state.data);
  const ui = useSelector((state) => state.ui);

  const {
    revision,
    statementContent: { elementCache, sectionIdList },
    selectedStatement: { isOCR, isCftEnabled },
  } = data;

  const {
    reportPreviewPage,
    previewPage: {
      shouldEnableRefreshIcon,
      visibleFirstElementFromTop,
      isSyncScrollingEnabled,
    },
    statementPage: { sectionsInView },
  } = ui;

  // const scrollLock = useRef(false);
  const [isRefreshing, setRefreshing] = useState(false);
  const [allList, setAllList] = useState([]);
  const [elementReportData, setElementReportData] = useState([]);

  const setPreviewPageDisplayOptionAction = ({ key }) => {
    const updatedState = {
      ...reportPreviewPage.reportPreviewPageDisplayOptions,
    };

    if (key === 'displayAll') {
      // Toggle the 'displayAll' option and update all other options accordingly.
      const newValue = !updatedState.displayAll;
      Object.keys(updatedState).forEach((k) => {
        if (k !== 'displayAll' && k !== 'elementId') {
          updatedState[k] = newValue;
        }
      });
      updatedState.displayAll = newValue;
    } else {
      // Toggle the specified option.
      updatedState[key] = !updatedState[key];

      // Update the 'displayAll' option based on the state of all other options.
      const allOptions = Object.keys(updatedState).filter(
        (k) => k !== 'displayAll' && k !== 'elementId',
      );
      const allTrue = allOptions.every((k) => updatedState[k]);
      const allFalse = allOptions.every((k) => !updatedState[k]);

      if (allTrue) {
        updatedState.displayAll = true;
      } else if (allFalse) {
        updatedState.displayAll = false;
      }
    }
    dispatch(setReportPreviewPageDisplayOptions(updatedState));

    return updatedState;
  };

  //updated sync scroll toggle when component mounts/unmount
  useEffect(() => {
    dispatch(setSyncScrolling());
    return () => {
      dispatch(resetSyncScrolling());
    };
  }, []);

  //get all elements data during component mount, no need to fetch everytime (expensive)
  useEffect(() => {
    dispatch(clearSelectedElementsContentPanel());
    let isMounted = true;
    if (revision.id && !elementReportData.length) {
      getAllElements({ revisionId: revision.id })
        .then((response) => {
          if (
            response &&
            response.data &&
            response.data.result &&
            response.data.result.allElements &&
            isMounted
          ) {
            setAllList(response.data.result.allElements);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision.id]);

  //collect all element data for the sections that are present in view.
  useEffect(() => {
    if (isSyncScrollingEnabled) {
      const sectionIdListInView = sectionsInView.sectionIds;
      const isElementLoaded = sectionIdListInView.some(
        (item) => elementCache[item] && elementCache[item].isLoaded === true,
      );
      if (!isElementLoaded) {
        console.log('data loading');
      } else {
        const elementList = getElementListBySections(
          sectionIdListInView,
          elementCache,
        );

        let filteredElementList = elementList.map((element) => element.data);
        filteredElementList = filteredElementList.sort((a, b) => {
          if (isNullOrUndefined(a.previewSequenceID)) return 1; // push null to end
          if (isNullOrUndefined(b.previewSequenceID))
            return -1; // push null to end
          else
            return (
              parseInt(a.previewSequenceID) - parseInt(b.previewSequenceID)
            ); // sort in ascending order
        });
        setElementReportData(filteredElementList);
      }
    } else {
      setElementReportData(allList);
    }
  }, [sectionsInView, elementCache, isSyncScrollingEnabled, allList.length]);

  const onRowClick = (rowData) => {
    dispatch(
      setSelectedElementDetails({
        selectedElementId: rowData.id,
        sectionId: rowData.sectionId,
        isClickedOnReport: true,
      }),
    );
    //scroll the left view
    scrollElementIntoView({
      elementId: rowData.id,
      sectionId: rowData.sectionId,
      sectionsInView: sectionsInView,
      isOCR: isOCR,
    });
  };

  //Logic when clicked on refresh icon
  const onRefreshData = () => {
    setRefreshing(true);
    try {
      if (isOCR) {
        let sectionMetaData = [];
        sectionsInView.sectionIds.forEach((id) => {
          sectionMetaData.push(
            sectionIdList.sectionIds.find(
              (section) => section.sectionId === parseInt(id),
            ),
          );
        });
        if (sectionMetaData.length > 0) {
          dispatch(fetchOCRSectionContent(sectionMetaData));
        }
      } else dispatch(fetchSectionContent(sectionsInView.sectionIds));
    } catch (ex) {
      console.info(
        'An error occured while refreshing section data in preview mode',
      );
      console.error(ex);
    } finally {
      setTimeout(() => {
        setRefreshing(false);
        dispatch(disableRefreshIconAction());
      }, 5000);
    }
  };

  useEffect(() => {
    dispatch(
      setReOrderedColumnOptions(
        reportPreviewPage.reOrderedcolumnList &&
          reportPreviewPage.reOrderedcolumnList.length
          ? reportPreviewPage.reOrderedcolumnList
          : OPTIONS_CHECKBOX_ARRAY,
      ),
    );
    let displayOptions =
      reportPreviewPage.reportPreviewPageDisplayOptions &&
      reportPreviewPage.reportPreviewPageDisplayOptions.length
        ? reportPreviewPage.reportPreviewPageDisplayOptions
        : displayOptionsConfig;

    if (!isNullOrUndefined(isCftEnabled) && !isCftEnabled) {
      delete displayOptions.comfortLetter;
    }

    dispatch(setReportPreviewPageDisplayOptions(displayOptions));
  }, [isCftEnabled]);

  return (
    <div className={REPORT_PREVIEW_RIGHT_BLOCK}>
      <div className={`${REPORT_PREVIEW_RIGHT_BLOCK}__element-report-header`}>
        <div
          className={classNames({
            [`${REPORT_PREVIEW_RIGHT_BLOCK}__element-report-header--title`]: true,
            [`${REPORT_PREVIEW_RIGHT_BLOCK}__element-report-header--title-reverse`]:
              toggleViews,
          })}
        >
          <FormattedMessage
            id={'statement.navigation.report.preview.right.view.subtitle'}
          />
        </div>
        <div className={`${REPORT_PREVIEW_RIGHT_BLOCK}__tools`}>
          <SlideToggle
            label="compare-panel.sync-scroll.toggle.label"
            name={'id'}
            onChange={() => dispatch(onToggleSyncScroll())}
            checked={isSyncScrollingEnabled}
            id={'sync-scroll-toggle'}
            className={`${REPORT_PREVIEW_RIGHT_BLOCK}__sync-scroll-toggle`}
          />
          {shouldEnableRefreshIcon && (
            <div
              className={`${REPORT_PREVIEW_RIGHT_BLOCK}__element-report-header--refresh-icon`}
            >
              <ToolTip
                id={`${REPORT_PREVIEW_RIGHT_BLOCK_ID}-tooltip-refresh-button`}
                text={
                  'workapaper-report-preview.page.element-report.refresh-tooltip'
                }
                position={'left'}
              >
                <RefreshIcon
                  className={classNames({
                    'rotate-icon': isRefreshing,
                  })}
                  onClick={() => {
                    shouldEnableRefreshIcon && !isRefreshing && onRefreshData();
                  }}
                  width={REFRESH_LIST_ICON_SIZE}
                  height={REFRESH_LIST_ICON_SIZE}
                />
              </ToolTip>
            </div>
          )}
          <ReportPreviewPageDisplayOptions
            reportPreviewPageDisplayOptions={
              reportPreviewPage.reportPreviewPageDisplayOptions
            }
            setPreviewPageDisplayOptionAction={
              setPreviewPageDisplayOptionAction
            }
            isCftEnabled={isCftEnabled}
            toggleViews={toggleViews}
            setReOrderedColumnOptions={setReOrderedColumnOptions}
            reOrederedOptions={reportPreviewPage.reOrderedcolumnList}
          />
        </div>
      </div>
      <div>
        <ReactGrid
          primaryKeyColumn={'id'}
          firstVisibleElementFromTop={parseInt(visibleFirstElementFromTop)}
          columns={getElementReoprtColumns({
            reportPreviewPageDisplayOptions:
              reportPreviewPage.reportPreviewPageDisplayOptions,
            isSyncScrollingEnabled,
            reOrederedOptions: reportPreviewPage.reOrderedcolumnList,
          })}
          data={elementReportData}
          selectedElementId={reportPreviewPage.selectedElementId}
          onRowDataClick={onRowClick}
        />
      </div>
    </div>
  );
};
export default memo(ReportPreviewRightSideView);
